import {
  TableWrapper,
  CardList,
  Card,
  CardField,
  CardFieldLabel,
  CardFieldValue,
  StatusBadge,
  Actions,
  Action,
  PaginationBar,
  PaginationInfo,
  PaginationControls,
  PaginationButton,
} from './Table.style';

export interface TableColumn<T = object> {
  key: string;
  label: string;
  render?: (value: unknown, row: T) => React.ReactNode;
  width?: number;
}

export interface TableProps<T = object> {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  /** Página atual (1-based). Use junto de `totalPages` e `onPageChange` para exibir paginação. */
  page?: number;
  /** Total de páginas. */
  totalPages?: number;
  onPageChange?: (page: number) => void;
  rowClickable?: boolean;
}

function Table<T extends object>({
  columns,
  data,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  rowClickable = false,
  getRowKey = (row, index) => {
    const r = row as unknown as { id?: string | number };
    return r.id != null ? String(r.id) : index;
  },
}: TableProps<T>) {
  const showPagination =
    page != null && totalPages != null && totalPages > 1 && onPageChange != null;

  const canPrev = showPagination && page > 1;
  const canNext = showPagination && page < totalPages;

  return (
    <TableWrapper>
      <CardList>
        {data.map((row, index) => (
          <Card
            key={String(getRowKey(row, index))}
            onClick={() => onRowClick?.(row)}
            $rowClickable={rowClickable}
          >
            {columns.map((col) => {
              const value = (row as Record<string, unknown>)[col.key];
              const content = col.render ? col.render(value, row) : (value as React.ReactNode);
              return (
                <CardField key={col.key} $width={col.width}>
                  <CardFieldLabel>{col.label}</CardFieldLabel>
                  <CardFieldValue>{content}</CardFieldValue>
                </CardField>
              );
            })}
          </Card>
        ))}
      </CardList>
      {showPagination && (
        <PaginationBar aria-label="Paginação da tabela">
          <PaginationControls>
            <PaginationButton
              type="button"
              $disabled={!canPrev}
              disabled={!canPrev}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Anterior
            </PaginationButton>
            <PaginationInfo>
              Página {page} de {totalPages}
            </PaginationInfo>
            <PaginationButton
              type="button"
              $disabled={!canNext}
              disabled={!canNext}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              Próxima
            </PaginationButton>
          </PaginationControls>
        </PaginationBar>
      )}
    </TableWrapper>
  );
}

Table.StatusBadge = StatusBadge;
Table.Actions = Actions;
Table.Action = Action;

export default Table;
